import { MOVES } from '../config/moveLibrary';

// bodyLanguage — what a resident does with their body when they are not on a
// stage (2026-11-04). Khari chose both: the artist's own body language, by
// their own choice (API city/bodyMenu).
//
//   TALKING   a line's own cue (`line.cue`, a move the artist put with the line)
//             plays as the line starts, for its length; otherwise they gesture as
//             they speak, more for a lively artist (`e`, their energy and
//             movement), less for a calm one. The other one listens: now and
//             then visibly, otherwise their own idle.
//   HABITS    standing around (free, or at a place in their day), now and then
//             one of the habits they chose this morning (`h`), for its length,
//             then their idle again. Before they have chosen: a few quiet ones.
//
// ⚠ THE SAME ON EVERY SCREEN: everything here is from the line schedule, the
// resident and the server clock, never from a viewer's audio or a random draw.

const SECONDS = new Map(MOVES.map((m) => [m.key, m.seconds]));
const lenMs = (key, cap = 12) => Math.round(Math.min(SECONDS.get(key) || 4, cap) * 1000);
const hash = (n) => { const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453; return x - Math.floor(x); };

// Talking with the hands, by how animated they are. Measured to stay in place
// and face the person they talk to (API city/bodyMenu notes).
const TALK = {
  calm: ['talk_with_left_hand_raised', 'talk_with_right_hand_open', 'stand_and_chat'],
  mid: ['talk_with_hands_open', 'talk_with_right_hand_open', 'talk_with_left_hand_on_hip', 'discuss_while_moving'],
  lively: ['talk_passionately', 'talk_with_hands_open', 'talk_with_left_hand_on_hip'],
};
const LISTEN = 'listening_gesture';
// A freestyle at the court (a conversation with a `beat`): the one rapping works
// it with their hands (recorded stage vocals that stay on the spot, hands low or
// an arm out), the other one bobs to the beat.
const RAP = ['perf_vocal_14', 'perf_vocal_15', 'perf_vocal_04', 'perf_vocal_07'];
const BOB = 'happy_sway_standing';
// Before they have chosen their habits: the quiet ones, for anyone.
const DEFAULT_HABITS = ['short_breathe_and_look_around', 'long_breathe_and_look_around', 'idle_03', 'listening_gesture'];
const HABIT_WINDOW_MS = 30000;

const levelOf = (e) => (e >= 65 ? 'lively' : e < 40 ? 'calm' : 'mid');

/**
 * Their move in a conversation at `t`, or null (between lines, or not in one).
 * @param conv  the conversation (cityStore convs[id], with `lines`)
 */
export function talkMove(conv, avatarId, t, animated = 50) {
  if (!conv?.lines?.length) return null;
  const me = Number(avatarId);
  for (const l of conv.lines) {
    if (!(l.playAt <= t)) continue;
    const end = l.playAt + (Number(l.dur) || 3) * 1000;
    if (t >= end) continue;
    const rap = !!conv.beat;
    if (Number(l.speaker) === me) {
      // Their own gesture first, for as long as it lasts (never past the line).
      if (l.cue && SECONDS.has(l.cue) && t < l.playAt + Math.min(lenMs(l.cue), end - l.playAt)) return l.cue;
      const set = rap ? RAP : TALK[levelOf(animated)];
      return set[Math.floor(hash(me * 31 + (Number(l.idx) || 0)) * set.length) % set.length];
    }
    if (rap) return BOB;
    // Listening to them: visibly on about half the lines (more for a lively listener).
    return hash(me * 17 + (Number(l.idx) || 0)) < 0.35 + animated / 400 ? LISTEN : null;
  }
  return null;
}

/* At a room's booth, in a set (VenueStage `doing: 'song'`): a club set grooves
   (no DJ move exists in the library: sways, steps and bounces that stay at the
   booth, bigger for a lively artist); a spoken-word piece is said with the hands.
   It changes every 16 s, the same on every screen. */
const BOOTH = {
  calm: ['happy_sway_standing', 'joyful_dance_with_hand_sway'],
  mid: ['happy_sway_standing', 'step_hip_hop_dance', 'joyful_dance_with_hand_sway', 'hip_hop_dance_3'],
  lively: ['hip_hop_dance', 'step_hip_hop_dance', 'joyful_dance_with_hand_sway', 'side_jumps_in_horse_stance'],
};
const SPOKEN = ['talk_passionately', 'talk_with_hands_open', 'talk_with_left_hand_raised'];
export function boothMove(avatarId, t, kind = 'club', animated = 50) {
  const set = kind === 'spoken' ? SPOKEN : BOOTH[levelOf(animated)];
  return set[Math.floor(hash(Number(avatarId) * 53 + Math.floor(t / 16000)) * set.length) % set.length];
}

/** Talking with a visitor (their reply being spoken, SceneChat): their hands, changing every few seconds. */
export function chatTalkMove(avatarId, t, animated = 50) {
  const set = TALK[levelOf(animated)];
  return set[Math.floor(hash(Number(avatarId) * 31 + Math.floor(t / 6000)) * set.length) % set.length];
}

/**
 * A habit at `t`, or null (their idle). Every 30 s window, a draw from the
 * resident and the window decides whether one plays and which; it plays once,
 * from a point inside the window, for its own length.
 */
export function habitMove(avatarId, habits, t, animated = 50) {
  const list = Array.isArray(habits) && habits.length ? habits.filter((k) => SECONDS.has(k)) : DEFAULT_HABITS;
  if (!list.length) return null;
  const id = Number(avatarId) || 0;
  const phase = Math.floor(hash(id + 3) * HABIT_WINDOW_MS);
  const w = Math.floor((t + phase) / HABIT_WINDOW_MS);
  if (hash(id * 7919 + w) >= 0.25 + animated / 400) return null;
  const key = list[Math.floor(hash(id * 104729 + w * 13) * list.length) % list.length];
  const len = lenMs(key);
  const start = w * HABIT_WINDOW_MS - phase + Math.floor(hash(id * 31337 + w) * Math.max(0, HABIT_WINDOW_MS - len));
  return t >= start && t < start + len ? key : null;
}

/* ── SEATED (2026-11-04: café guests, helicopter passengers) ──────────────────
   Khari: "Seated café guests: use seated gestures if any … seated characters in
   helicopters should use a seated animation. user included. As of now, they seem
   to sit in a t-pose." The procedural sitting poses wrote offsets onto each
   rig's own bind pose, and a rig bound arms-out sat arms-out; these are recorded
   seated moves, fitted to each body like every library move (AnimatedResident
   `seatHips` puts their hips on the seat). Measured: all keep the hips within
   2 cm of their average, so a body stays on its cushion.
     idle     the two recorded seated idles, tagged F and M in the move table:
              the one matching how the body presents, as its owner set it
              (API avatar-models `presents`; a mannequin's group); not known, a
              steady draw per body
     talking  sitting_answering_questions: talking with the hands, seated
     habits   now and then at a café table: a sip of a drink, a lean back with
              the hands behind the head, the arms crossed */
const SEATED_IDLE = { f: 'chair_sit_idle_f', m: 'chair_sit_idle_m' };
const SEATED_TALK = 'sitting_answering_questions';
const SEATED_HABITS = ['sit_and_drink', 'sit_hands_on_head_lean_back', 'sit_on_chair_arms_crossed'];
export const seatedIdle = (presents, avatarId = 0) => SEATED_IDLE[presents] || (hash(Number(avatarId) + 11) < 0.5 ? SEATED_IDLE.f : SEATED_IDLE.m);
/** A seated body's move at `t`: talking, else (with `habits`) now and then a seated habit, else their seated idle. Never null. */
export function seatedMove(avatarId, presents, t, { talking = false, habits = false, animated = 50 } = {}) {
  if (talking) return SEATED_TALK;
  if (habits) { const h = habitMove(avatarId, SEATED_HABITS, t, animated); if (h) return h; }
  return seatedIdle(presents, avatarId);
}

/* ── ON REQUEST (2026-11-04, API city/moveBook) ───────────────────────────────
   A move the artist chose to do (the `do_move` tool; socket `city_emote`): it
   plays once, from `at` for `dur` seconds, over anything else they are doing
   except walking. Same moment on every screen (the server clock). Several in a
   row are a list (cityStore addEmote), each starting where the last ends. */
export function emoteMove(emotes, avatarId, t) {
  const list = emotes?.[Number(avatarId)];
  const e = (Array.isArray(list) ? list : []).find((x) => SECONDS.has(x.move) && t >= x.at && t < x.at + x.dur * 1000);
  return e ? e.move : null;
}
